// @catalog F.006
// TopProductsTable — estilo handoff (plan dashboard_executive_redesign 2026-05-21).
// Headers uppercase letterspaced · SKU mono badge · DeltaPill limpio.
// Incluye filtro por nombre + columnas sortables (toggle asc/desc).
import { useMemo } from "react";
import { Alert, Box, Stack } from "@mui/material";
import AnalyticsService, {
  type TopProductMonthly,
} from "../../services/analytics.service";
import {
  TableShell,
  HeaderCell,
  BodyRow,
  BodyCell,
  RankNum,
  ProductCell,
  DeltaPill,
} from "../analytics/TableCells";
import {
  SortableHeaderCell,
  TableFilterInput,
  useTableControls,
} from "../analytics/TableControls";

interface TopProductsTableProps {
  products: TopProductMonthly[];
  // Callback opcional al click en una fila. Si se provee, la fila es
  // clickeable y abre el ProductDetailDialog del padre.
  onProductClick?: (productId: number, productName: string) => void;
}

// Heurística para extraer SKU desde el inicio del nombre (formato VillApps:
// "<CODE> <DESCRIPCIÓN>" — separador es espacio). Si no hay patrón, devuelve null.
function splitNameSku(fullName: string): { sku: string | null; name: string } {
  const trimmed = fullName.trim();
  const firstSpace = trimmed.indexOf(" ");
  if (firstSpace === -1) return { sku: null, name: trimmed };
  const possibleSku = trimmed.slice(0, firstSpace);
  if (
    possibleSku.length >= 3 &&
    possibleSku.length <= 20 &&
    /[\d-]/.test(possibleSku)
  ) {
    return { sku: possibleSku, name: trimmed.slice(firstSpace + 1) };
  }
  return { sku: null, name: trimmed };
}

export function TopProductsTable({
  products,
  onProductClick,
}: TopProductsTableProps): JSX.Element {
  const filterAccessor = useMemo(
    () => (p: TopProductMonthly) =>
      AnalyticsService.extractProductName(p.productName),
    [],
  );
  const sortAccessors = useMemo(
    () => ({
      product: (p: TopProductMonthly) =>
        AnalyticsService.extractProductName(p.productName),
      unitsSold: (p: TopProductMonthly) => p.unitsSold,
      avgPrice: (p: TopProductMonthly) => p.avgPrice,
      totalSales: (p: TopProductMonthly) => p.totalSales,
      ordersCount: (p: TopProductMonthly) => p.ordersCount,
      growth: (p: TopProductMonthly) =>
        p.growthPercentage ?? Number.NEGATIVE_INFINITY,
    }),
    [],
  );

  const { filter, setFilter, sort, toggleSort, processedData } =
    useTableControls<TopProductMonthly>({
      data: products ?? [],
      filterAccessor,
      sortAccessors,
      defaultSort: { key: "totalSales", dir: "desc" },
    });

  if (!products || products.length === 0) {
    return (
      <Alert severity="info">
        No hay datos de productos para el mes actual.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <TableFilterInput
        value={filter}
        onChange={setFilter}
        placeholder="Buscar producto…"
        helper={
          filter
            ? `${processedData.length} de ${products.length}`
            : `${products.length} productos`
        }
      />
      {processedData.length === 0 ? (
        <Alert severity="info">
          Sin resultados para "{filter}".
        </Alert>
      ) : (
        <TableShell maxHeight={500}>
          <thead>
            <tr>
              <HeaderCell width={40} align="right">
                #
              </HeaderCell>
              <SortableHeaderCell
                sortKey="product"
                currentSort={sort}
                onSort={toggleSort}
              >
                Producto
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="unitsSold"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Unidades
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="avgPrice"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Precio Prom.
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="totalSales"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Facturación
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="ordersCount"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Órdenes
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="growth"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Vs. Año ant.
              </SortableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {processedData.map((product, index) => {
              const fullName = AnalyticsService.extractProductName(
                product.productName,
              );
              const { sku, name } = splitNameSku(fullName);
              const handleClick = onProductClick
                ? () => onProductClick(product.productId, fullName)
                : undefined;
              return (
                <BodyRow
                  key={`${product.productId}-${index}`}
                  {...(handleClick && { onClick: handleClick })}
                >
                  <BodyCell align="right">
                    <RankNum n={index + 1} />
                  </BodyCell>
                  <BodyCell>
                    <ProductCell
                      sku={sku}
                      name={name}
                      subtitle={
                        product.categoryName && product.categoryName !== "All"
                          ? product.categoryName
                          : null
                      }
                    />
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {AnalyticsService.formatNumber(product.unitsSold)}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {AnalyticsService.formatCurrency(product.avgPrice)}
                  </BodyCell>
                  <BodyCell align="right">
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {AnalyticsService.formatCurrency(product.totalSales)}
                    </Box>
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {AnalyticsService.formatNumber(product.ordersCount)}
                  </BodyCell>
                  <BodyCell align="right">
                    <DeltaPill delta={product.growthPercentage ?? null} />
                  </BodyCell>
                </BodyRow>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </Stack>
  );
}
